import { FC, useEffect, useRef } from "react";
import { isMobile as isMobileBrowser } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { Dialog } from "../../core/Dialog/Dialog";
import { Button } from "../../core/Button/Button";
import { Icon } from "../../core/Icon/Icon";
import { Image } from "../../core/Image/Image";
import { TextField } from "../../core/TextField/TextField";
import { Toggle } from "../../core/Toggle/Toggle";
import { Typography } from "../../core/Typography/Typography";
import { SettingRow } from "../SettingRow";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { PROFILE_NAME_MAX_LENGTH, ProfileDraft } from "../../../models/profile";
import { isKidModeDisabled } from "../../../utils/platform";
import { useAppSelector } from "../../hooks";
import { isIOS, isTV } from "../../../utils/platform";
import { useStyles } from "./ProfileFormPopup.style";

export type ProfileFormField = "avatar" | "profileLock" | "remove";

interface IProfileFormPopupProps {
  open: boolean;
  mode: "new" | "edit";
  draft: ProfileDraft;
  nameError?: string;
  canSubmit: boolean;
  showKidMode: boolean;
  profileLockControl?: "toggle" | "manage";
  showRemove: boolean;
  focusField?: ProfileFormField;
  onChangeName: (name: string) => void;
  onToggleKidMode: (isKid: boolean) => void;
  onToggleProfileLock: (hasLock: boolean) => void;
  onManageProfileLock: () => void;
  onOpenAvatar: () => void;
  onRemove: () => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const ProfileFormPopup: FC<IProfileFormPopupProps> = ({
  open,
  mode,
  draft,
  nameError,
  canSubmit,
  showKidMode,
  profileLockControl,
  showRemove,
  focusField,
  onChangeName,
  onToggleKidMode,
  onToggleProfileLock,
  onManageProfileLock,
  onOpenAvatar,
  onRemove,
  onSubmit,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const inputMode = useAppSelector((state) => state.appState.inputMode);
  const nameFieldRef = useRef<HTMLDivElement>(null);

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open || focusField || inputMode === "gamepad") return;
    if (isTV || isMobileBrowser || isIOS) return;
    const frame = requestAnimationFrame(() =>
      nameFieldRef.current?.querySelector("input")?.focus(),
    );
    return () => cancelAnimationFrame(frame);
  }, [open, focusField]);

  const gamepadId = (field: ProfileFormField, id: string) =>
    field === focusField ? "default-selected" : id;

  return (
    <Dialog
      open={open}
      data-dialog-nav="rows"
      title={
        isEdit ? t("ProfileForm_Edit Profile") : t("ProfileForm_New Profile")
      }
      onClose={onClose}
      mainAction={{
        text: isEdit ? t("ProfileForm_Save") : t("ProfileForm_Create"),
        onClick: onSubmit,
        disabled: !canSubmit,
        "data-gamepad": "profile-form-submit",
      }}
      secondAction={{
        text: t("ProfileForm_Cancel"),
        onClick: onClose,
        "data-gamepad": "profile-form-cancel",
      }}
    >
      <div className={classes.content}>
        <div className={classes.header}>
          <Typography
            variant={TypographyVariant.HeadingH5Bold}
            className={classes.title}
          >
            {isEdit
              ? t("ProfileForm_Edit Profile")
              : t("ProfileForm_New Profile")}
          </Typography>
          {!isEdit && (
            <Typography
              variant={TypographyVariant.BodyB6Medium}
              color={TypographyColor.Silver}
              className={classes.description}
            >
              {t("ProfileForm_Create a profile for someone else using ONMO+")}
            </Typography>
          )}
        </div>

        <div
          className={classes.avatarButton}
          onClick={onOpenAvatar}
          role="button"
          tabIndex={-1}
          aria-label={t("ChooseAvatar_Choose Avatar")}
          data-gamepad={gamepadId("avatar", "profile-form-avatar")}
          data-selected-class={classes.selected}
        >
          {draft.avatar ? (
            <Image
              alt=""
              src={draft.avatar}
              className={classes.avatarPreview}
            />
          ) : (
            <span className={`icon-bold-user ${classes.avatarPlaceholder}`} />
          )}
          <Icon
            name="icon-bold-edit"
            size={24}
            className={classes.avatarIcon}
          />
        </div>

        <div className={classes.field} ref={nameFieldRef}>
          <Typography
            variant={TypographyVariant.BodyB6Regular}
            color={TypographyColor.Silver}
          >
            {t("ProfileForm_Profile Name")}
          </Typography>
          <TextField
            fullWidth
            maxLength={PROFILE_NAME_MAX_LENGTH}
            isError={!!nameError}
            descriptionMessage={nameError}
            onChangeValue={onChangeName}
            inputProps={{
              value: draft.name,
              placeholder: t("ProfileForm_[ProfileName]"),
              "aria-label": t("ProfileForm_Profile Name"),
              dataGamepad: focusField
                ? "profile-form-name"
                : "default-selected",
            }}
          />
        </div>

        {showKidMode && (
          <SettingRow
            title={t("ProfileForm_Kid Mode")}
            description={
              isKidModeDisabled
                ? t("ProfileForm_Coming Soon... Stay tuned!")
                : t(
                    "ProfileForm_When turned on, it automatically filters out content rated above 12+ and disables Install & Play along with Steam linking.",
                  )
            }
            control={
              isKidModeDisabled ? (
                <Toggle
                  checked={false}
                  disabled
                  aria-label={t("ProfileForm_Kid Mode")}
                />
              ) : (
                <Toggle
                  checked={draft.isKid}
                  onChange={(checked) => onToggleKidMode(!!checked)}
                  data-gamepad="profile-form-kid-mode"
                  aria-label={t("ProfileForm_Kid Mode")}
                  data-selected-class={classes.toggleSelected}
                />
              )
            }
          />
        )}

        {profileLockControl && (
          <SettingRow
            title={t("ProfileForm_Profile Lock")}
            description={t("ProfileForm_Lock your profile with a PIN Code.")}
            control={
              profileLockControl === "toggle" ? (
                <Toggle
                  checked={draft.hasLock}
                  onChange={(checked) => onToggleProfileLock(!!checked)}
                  data-gamepad={gamepadId("profileLock", "profile-form-lock")}
                  aria-label={t("ProfileForm_Profile Lock")}
                  data-selected-class={classes.toggleSelected}
                />
              ) : (
                <Button
                  size="small"
                  className={classes.rowButton}
                  onClick={onManageProfileLock}
                  data-gamepad={gamepadId(
                    "profileLock",
                    "profile-form-manage-lock",
                  )}
                >
                  {t("ProfileForm_Manage")}
                </Button>
              )
            }
          />
        )}

        {showRemove && (
          <SettingRow
            title={t("ProfileForm_Remove Profile")}
            description={t(
              "ProfileForm_Removing this profile from your account",
            )}
            control={
              <Button
                size="small"
                className={classes.rowButton}
                onClick={onRemove}
                data-gamepad={gamepadId("remove", "profile-form-remove")}
              >
                {t("ProfileForm_Remove")}
              </Button>
            }
          />
        )}
      </div>
    </Dialog>
  );
};
